import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

/**
 * Map frontend values to backend expected values
 */
const mapFiltersToBackend = (filters) => {
  // Map document type
  const documentTypeMap = {
    'all': 'All',
    'ticket_de_caisse': 'Receipt',
    'invoice': 'Invoice',
  };

  // Map groupBy
  const groupByMap = {
    'day': 'Daily',
    'week': 'Weekly',
    'month': 'Monthly',
    'quarter': 'Quarterly',
    'year': 'Yearly',
  };

  // Map payment method
  const paymentMethodMap = {
    'all': 'All',
    'cash': 'Cash',
    'virement': 'Virement',
    'bank_transfer': 'BankTransfer',
    'cheque': 'Cheque',
    'credit_card': 'CreditCard',
  };

  return {
    startDate: filters.startDate,
    endDate: filters.endDate,
    documentType: documentTypeMap[filters.documentType?.toLowerCase()] || 'All',
    groupBy: groupByMap[filters.groupBy?.toLowerCase()] || 'Daily',
    paymentMethod: paymentMethodMap[filters.paymentMethod?.toLowerCase()] || 'All',
  };
};

/**
 * Helper to safely convert values to numbers
 */
const safeNumber = (val) => {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
};

/**
 * Generate sales report based on filters
 * @param {Object} filters - Report filters (startDate, endDate, documentType, groupBy, paymentMethod)
 * @returns {Promise} - Report data
 */
export const generateSalesReport = async (filters) => {
  try {
    // Map frontend filters to backend expected format
    const backendFilters = mapFiltersToBackend(filters);
    
    console.log('📤 Frontend filters:', filters);
    console.log('📤 Backend mapped filters:', backendFilters);

    const response = await axiosInstance.post('/Reports/sales', backendFilters);

    console.log('📥 Backend response:', response.data);

    // Transform backend response to match frontend expectations
    const backendData = response.data.payload;
    
    // Check if we have any data
    if (backendData.TicketDetails.length === 0 && backendData.InvoiceDetails.length === 0) {
      console.warn('⚠️ No data found for the selected filters');
    }
    
    // Transform payment breakdown from array to object - USE BACKEND PERCENTAGE
    const paymentMethods = {};
    backendData.PaymentBreakdown.forEach(payment => {
      paymentMethods[payment.PaymentMethod] = {
        amount: safeNumber(payment.Amount),
        percentage: safeNumber(payment.Percentage), // Use backend percentage directly
        transactionCount: safeNumber(payment.TransactionCount),
      };
    });

    console.log('💰 Payment Methods Mapped:', paymentMethods);

    // Transform VAT breakdown from array to object
    const vatBreakdown = {};
    backendData.VATBreakdown.forEach(vat => {
      vatBreakdown[vat.VATRate] = {
        subtotal: safeNumber(vat.SubtotalExclVAT),
        vat: safeNumber(vat.VATAmount),
        total: safeNumber(vat.TotalInclVAT),
      };
    });

    // Combine ticket and invoice details into single array
    const details = [
      ...backendData.TicketDetails.map(ticket => ({
        date: ticket.DateTime,
        receiptNumber: ticket.TicketNumber,
        documentType: 'Ticket de Caisse',
        customerName: ticket.CustomerName === 'Walk-in' ? null : ticket.CustomerName,
        subtotal: ticket.Subtotal,
        vat: ticket.VAT,
        total: ticket.Total,
        paymentMethod: ticket.PaymentMethod,
        paymentStatus: ticket.PaymentStatus,
        paidAmount: safeNumber(ticket.PaidAmount),
        dueAmount: safeNumber(ticket.DueAmount),
        notes: ticket.Notes,
      })),
      ...backendData.InvoiceDetails.map(invoice => ({
        date: invoice.DateTime,
        receiptNumber: invoice.InvoiceNumber,
        documentType: 'Invoice',
        customerName: invoice.CustomerName,
        subtotal: safeNumber(invoice.Subtotal),
        vat: safeNumber(invoice.VAT),
        total: safeNumber(invoice.Total),
        paymentMethod: invoice.PaymentMethod || 'N/A',
        paymentStatus: invoice.PaymentStatus,
        paidAmount: safeNumber(invoice.PaidAmount),
        dueAmount: safeNumber(invoice.DueAmount),
        notes: invoice.Notes,
      })),
    ];

    // Sort by date (newest first)
    details.sort((a, b) => new Date(b.date) - new Date(a.date));

    const transformedData = {
      summary: {
        totalSales: safeNumber(backendData.Summary.TotalSales),
        totalVAT: safeNumber(backendData.Summary.TotalVAT),
        totalTransactions: safeNumber(backendData.Summary.Transactions),
        averageSale: safeNumber(backendData.Summary.AvgSale),
        ticketDeCaisseCount: safeNumber(backendData.Summary.TotalTickets),
        invoiceCount: safeNumber(backendData.Summary.TotalInvoices),
        totalSalesExclVAT: safeNumber(backendData.Summary.TotalSalesExclVAT),
        totalPaid: safeNumber(backendData.Summary.TotalPaid),
        totalDue: safeNumber(backendData.Summary.TotalDue),
      },
      vatBreakdown,
      paymentMethods,
      details,
      metadata: {
        generatedAt: backendData.Metadata.GeneratedAt,
        reportPeriod: backendData.Metadata.ReportPeriod,
      },
    };

    console.log('✅ Transformed data:', transformedData);

    return transformedData;
  } catch (error) {
    console.error('❌ Error generating sales report:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
};